import React, { useState } from 'react';
import {
  Box, Avatar, Typography, TextField, Button, Container, IconButton,
  InputAdornment
} from '@mui/material';
import { ArrowBack, Close as CloseIcon, Check as CheckIcon, Badge, Assignment } from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import AuthService from "../Authentication/auth.service";
import AlertService from "../../components/Alerts/alerts";
import myflag from "../../assets/myflag.png";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';


const EditProfile = ({ open, onClose, userData, onUpdate }) => {
  const [editedUser, setEditedUser] = useState(userData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();

  const inputStyle = {
    backgroundColor: '#F0F5FB',
    borderRadius: '8px',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiInputBase-input': {
      padding: '10px 10px',
    },
  };

  const inputStyleEmail = {
    ...inputStyle,
    backgroundColor: '#EDEDED',
  };

  const buttonStyle = {
    borderRadius: '8px',
    padding: '8px 16px',
    textTransform: 'none',
    fontWeight: 'bold',
    boxShadow: 'none',
    width: '30%',
  };

  const validateMmcNumber = (mmcNumber) => {
    if (!mmcNumber) return true; // Optional field
    const mmcRegex = /^[0-9]{4,6}$/; // Must be 4 to 6 digits
    return mmcRegex.test(mmcNumber);
  };

  const validateName = (name) => {
    if (!name || name.trim().length < 2) {
      return t('Name must be at least 2 characters long.');
    }
    if (name.trim().length > 50) {
      return t('Name must be less than 50 characters.');
    }
    const nameRegex = /^[a-zA-Z\s'-]+$/;
    if (!nameRegex.test(name.trim())) {
      return t('Name can only contain letters, spaces, hyphens, and apostrophes.');
    }
    return null;
  };

  const validatePhoneNumber = (phone) => {
    if (!phone) {
      return t('Phone number is required.');
    }
    // Ensure the phone number starts with '+'
    const formattedPhone = phone.startsWith('+') ? phone : `+${phone}`;
    const phoneRegex = /^\+[1-9]{1}[0-9]{3,14}$/;
    if (!phoneRegex.test(formattedPhone)) {
      return t('Please enter a valid phone number with country code.');
    }
    return null;
  };

  const validateApcNumber = (apcNumber) => {
    if (!apcNumber) return null; // Optional field
    if (apcNumber.trim().length < 3) {
      return t('APC Number must be at least 3 characters long.');
    }
    if (apcNumber.trim().length > 20) {
      return t('APC Number must be less than 20 characters.');
    }
    return null;
  };

  // Function to validate a single field and update errors state
  const validateField = (fieldName, value) => {
    let error = null;
    
    switch (fieldName) {
      case 'name':
        error = validateName(value);
        break;
      case 'phone':
        error = validatePhoneNumber(value);
        break;
      case 'mmc_number':
        if (value && !validateMmcNumber(value)) {
          error = t('MMC Number must be 4 to 6 digits.');
        }
        break;
      case 'apc_number':
        error = validateApcNumber(value);
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));

    return error;
  };

  const handleUpdateProfile = async () => {
    // Validate all fields before proceeding
    const nameError = validateName(editedUser.name);
    if (nameError) {
      AlertService.swalErrorAlert(nameError);
      return;
    }

    const phoneError = validatePhoneNumber(editedUser.phone);
    if (phoneError) {
      AlertService.swalErrorAlert(phoneError);
      return;
    }

    // Validate MMC Number if provided
    if (editedUser.mmc_number && !validateMmcNumber(editedUser.mmc_number)) {
      AlertService.swalErrorAlert(t('MMC Number must be 4 to 6 digits.'));
      return;
    }

    const apcError = validateApcNumber(editedUser.apc_number);
    if (apcError) {
      AlertService.swalErrorAlert(apcError);
      return;
    }

    setLoading(true);
    try {
      // Ensure the phone number starts with '+'
      if (!editedUser.phone.startsWith('+')) {
        editedUser.phone = `+${editedUser.phone}`;
      }

      // Trim whitespace from name and other fields, exclude password
      const cleanedUserData = {
        _id: editedUser._id,
        name: editedUser.name.trim(),
        phone: editedUser.phone,
        mmc_number: editedUser.mmc_number || '',
        apc_number: editedUser.apc_number ? editedUser.apc_number.trim() : '',
        user_id: editedUser._id
      };

      const response = await AuthService.Update_User(cleanedUserData);
      if (response.data.status) {
        AlertService.swalSuccessAlert(t("Profile updated successfully."));
        localStorage.setItem("user", JSON.stringify(cleanedUserData));
        onUpdate(cleanedUserData); // Pass updated data back to parent
        onClose();
      } else {
        AlertService.swalErrorAlert(response.data.message || t("Error updating profile."));
      }
    } catch (error) {
      console.error("Profile update error:", error);
      AlertService.swalErrorAlert(t("Error updating profile. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        <IconButton
          sx={{ position: 'absolute', right: 8, top: 8 }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h5" align="center" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>
          Edit Profile
        </Typography>

        <Container maxWidth="xs" sx={{ pt: 2, pb: 3 }}>
          <Avatar
            sx={{
              bgcolor: '#184363',
              width: 60,
              height: 60,
              fontSize: 40,
              margin: '0 auto',
              marginBottom: 3,
              borderRadius: '10px',
            }}
          >
            {editedUser.name.charAt(0).toUpperCase()}
          </Avatar>

          <Box component="form" noValidate>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '13px' }}>
              Full Name
            </Typography>
            <TextField
              fullWidth
              value={editedUser.name}
              onChange={(e) => {
                const value = e.target.value;
                setEditedUser({ ...editedUser, name: value });
                validateField('name', value);
              }}
              variant="outlined"
              sx={{
                ...inputStyle,
                ...(errors.name && { 
                  '& .MuiOutlinedInput-root': { 
                    border: '1px solid #d32f2f' 
                  } 
                })
              }}
              margin="dense"
              error={!!errors.name}
              helperText={errors.name}
            />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '13px', mt: 1 }}>
              Email
            </Typography>
            <TextField
              fullWidth
              value={editedUser.email}
              disabled
              variant="outlined"
              sx={inputStyleEmail}
              margin="dense"
            />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '13px', mt: 1 }}>
              Phone Number
            </Typography>
            <PhoneInput
              country={'my'} // Default country: Malaysia
              value={editedUser.phone}
              onChange={(value) => {
                setEditedUser({ ...editedUser, phone: value });
                validateField('phone', value);
              }}
              inputStyle={{
                width: '100%',
                backgroundColor: '#F0F5FB',
                borderRadius: '8px',
                border: errors.phone ? '1px solid #d32f2f' : 'none',
              }}
              dropdownStyle={{
                borderRadius: '8px',
              }}
              inputProps={{
                name: 'phone',
                required: true,
              }}
            />
            {errors.phone && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {errors.phone}
              </Typography>
            )}

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '13px', mt: 1 }}>
              MMC Number (Optional)
            </Typography>
            <TextField
              fullWidth
              value={editedUser.mmc_number || ''}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                if (value.length <= 6) { // Max 6 digits
                  setEditedUser({ ...editedUser, mmc_number: value });
                  validateField('mmc_number', value);
                }
              }}
              variant="outlined"
              sx={{
                ...inputStyle,
                ...(errors.mmc_number && { 
                  '& .MuiOutlinedInput-root': { 
                    border: '1px solid #d32f2f' 
                  } 
                })
              }}
              margin="dense"
              placeholder="Enter 4-6 digits"
              error={!!errors.mmc_number}
              helperText={errors.mmc_number || "4-6 digits required"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Badge sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '13px', mt: 1 }}>
              APC Number (Optional)
            </Typography>
            <TextField
              fullWidth
              value={editedUser.apc_number || ''}
              onChange={(e) => {
                const value = e.target.value;
                setEditedUser({ ...editedUser, apc_number: value });
                validateField('apc_number', value);
              }}
              variant="outlined"
              sx={{
                ...inputStyle,
                ...(errors.apc_number && { 
                  '& .MuiOutlinedInput-root': { 
                    border: '1px solid #d32f2f' 
                  } 
                })
              }}
              margin="dense"
              placeholder="Enter APC Number"
              error={!!errors.apc_number}
              helperText={errors.apc_number}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Assignment sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                onClick={onClose}
                sx={{
                  ...buttonStyle,
                  marginRight: '10px',
                  backgroundColor: '#F0F0F0',
                  color: '#333',
                }}
                startIcon={<CloseIcon />}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateProfile}
                disabled={loading || Object.values(errors).some(error => error !== null)}
                sx={{
                  ...buttonStyle,
                  backgroundColor: '#3488A4',
                  color: 'white',
                  opacity: Object.values(errors).some(error => error !== null) ? 0.6 : 1,
                }}
                startIcon={<CheckIcon />}
              >
                Update
              </Button>
            </Box>
          </Box>
        </Container>
      </div>
    </div>
  );
};

export default EditProfile;